import { Link } from 'react-router';
import { ScrollReveal } from '@podus/ui';
import { PRODUCT_URLS } from '@podus/utils';
import { POSTS } from '@/blog/posts';

/**
 * The homepage's topical anchor for "AI for justice", "AI for good" and "tech for good".
 *
 * These are the terms we want the landing page itself to rank for, and before this section the
 * homepage carried them only in <meta> tags — which search engines ignore for ranking. The copy
 * here is deliberately definitional: it opens by answering "what is AI for justice?" in one
 * sentence so the paragraph can be lifted as a featured snippet.
 *
 * It is also the only route into /blog from anywhere on the site. Without these links the posts
 * are orphaned: crawlable via the sitemap, but passing no authority to or from the homepage.
 */
export default function AiForJustice() {
    const pillars = [
        {
            term: 'AI for Justice',
            body: 'Ask a legal question in plain language, learn where you stand, and draft the petition, Freedom of Information request or complaint letter that obliges an institution to respond. Podus AI is tuned to move fastest on wrongful arrest, police extortion, human rights violations and gender-based violence.',
        },
        {
            term: 'AI for Good',
            body: 'Once a model is trained on the law, the cost of answering one more person is close to zero. That is why Podus AI is free to use and why we intend to keep it that way. We measure AI for good in cases moved forward, not in claims made.',
        },
        {
            term: 'Tech for Good',
            body: 'Technology aimed at civic problems rather than commercial ones. Since 2017 Citizens’ Gavel has built the volunteer lawyer network, the case tools and now the AI assistant that all sit behind one idea: legal help should not depend on your income.',
        },
    ];

    return (
        <section id="ai-for-justice" className="px-4 py-12 sm:px-8 sm:py-16 lg:px-16 lg:py-24 xl:px-20">
            <div className="mx-auto max-w-[1920px]">
                <ScrollReveal variant="fade-up" delay={0} distance={30}>
                    <div className="max-w-3xl">
                        <p className="text-[13px] font-semibold tracking-[0.18em] text-primary uppercase sm:text-[14px]">
                            Why we build
                        </p>
                        <h2 className="mt-4 text-[clamp(1.8rem,4.5vw,3.2rem)] font-bold leading-[1.2] tracking-tight text-[#2D2D2D]">
                            AI for Justice, built as tech for good
                        </h2>
                        <p className="mt-5 text-[15px] leading-[2] text-[#4E4D4D] sm:text-[16px]">
                            <strong>AI for justice</strong> means putting artificial intelligence to work on a problem
                            markets have never solved: helping ordinary people understand and enforce their legal
                            rights. Podus AI is a free AI legal assistant built by Citizens&rsquo; Gavel, trained on
                            Nigerian law and reachable over WhatsApp. It is the newest part of the access-to-justice
                            work we have done since 2017, across more than 6,500 legal interventions.
                        </p>
                    </div>
                </ScrollReveal>

                <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:mt-14 lg:grid-cols-3">
                    {pillars.map((pillar, i) => (
                        <ScrollReveal key={pillar.term} variant="fade-up" delay={120 * (i + 1)} distance={30}>
                            <div className="flex h-full flex-col rounded-[15px] bg-[#f5f5f5] p-6 sm:p-8">
                                <h3 className="text-[20px] font-bold leading-snug text-secondary sm:text-[22px]">
                                    {pillar.term}
                                </h3>
                                <p className="mt-4 text-[14px] leading-[1.9] text-[#4E4D4D] sm:text-[15px]">
                                    {pillar.body}
                                </p>
                            </div>
                        </ScrollReveal>
                    ))}
                </div>

                <ScrollReveal variant="fade-up" delay={200} distance={30}>
                    <div className="mt-10 flex flex-col gap-8 rounded-[15px] border border-[#ececec] p-6 sm:p-8 lg:mt-14 lg:flex-row lg:items-center lg:justify-between lg:gap-12">
                        <div className="lg:max-w-[52%]">
                            <h3 className="text-[18px] font-bold text-secondary sm:text-[20px]">
                                More from the Podus AI team
                            </h3>
                            <ul className="mt-4 flex flex-col gap-3">
                                {POSTS.map((post) => (
                                    <li key={post.slug} className="flex gap-3">
                                        <span
                                            aria-hidden="true"
                                            className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
                                        />
                                        <Link
                                            to={`/blog/${post.slug}`}
                                            className="text-[14px] leading-[1.7] font-semibold text-[#333] transition-colors hover:text-primary sm:text-[15px]"
                                        >
                                            {post.title}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                            <Link
                                to="/blog"
                                className="mt-5 inline-flex items-center gap-2 text-[14px] font-semibold text-secondary transition-colors hover:text-primary"
                            >
                                Read all articles <span aria-hidden="true">&rarr;</span>
                            </Link>
                        </div>

                        <div className="lg:shrink-0">
                            <p className="text-[15px] font-semibold text-secondary">Get legal guidance in minutes.</p>
                            <p className="mt-2 max-w-sm text-[14px] leading-[1.7] text-[#747474]">
                                Free, trained on Nigerian law, and available on WhatsApp, web and mobile.
                            </p>
                            <a
                                href={PRODUCT_URLS.chatpodusai}
                                className="mt-5 inline-flex items-center justify-center rounded-full bg-secondary px-8 py-3.5 text-[14px] font-bold text-white transition-transform hover:scale-[1.02] active:scale-[0.98]"
                            >
                                Try Podus AI
                            </a>
                        </div>
                    </div>
                </ScrollReveal>
            </div>
        </section>
    );
}
